import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@shadcn-in-nx/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PromptReviewModal({ open, onClose, analysis }) {
    const { t } = useTranslation();
    const [prompt, setPrompt] = useState(analysis?.suggestedPrompt || "");
    const { call: updatePrompt, isLoading } = useBluelagoon({ method: "updateSupportPrompt" });

    const handleApply = async () => {
        const res = await updatePrompt({
            newPrompt: prompt,
            reason: analysis?.changesSummary || "Applied from denial analysis",
        }).catch(() => null);
        if (res?.status === 200) {
            toast.success(t("admin.support.promptUpdated", "System prompt updated"));
            onClose();
        } else {
            toast.error(t("admin.support.promptUpdateError", "Failed to update prompt"));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t("admin.support.promptReview", "Prompt Improvement Suggestion")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {analysis?.analysis && (
                        <div>
                            <h4 className="text-sm font-medium mb-1">{t("admin.support.analysis", "Analysis")}</h4>
                            <p className="text-sm text-muted-foreground bg-muted p-3 rounded">{analysis.analysis}</p>
                        </div>
                    )}
                    {analysis?.changesSummary && (
                        <div>
                            <h4 className="text-sm font-medium mb-1">{t("admin.support.changes", "Changes Summary")}</h4>
                            <p className="text-sm text-muted-foreground bg-muted p-3 rounded">{analysis.changesSummary}</p>
                        </div>
                    )}
                    <div>
                        <h4 className="text-sm font-medium mb-1">{t("admin.support.suggestedPrompt", "Suggested Prompt")}</h4>
                        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={10} className="text-sm font-mono" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t("common.dismiss", "Dismiss")}</Button>
                    <Button onClick={handleApply} disabled={isLoading || !prompt.trim()}>
                        {isLoading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                        {t("admin.support.applyChanges", "Apply Changes")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
